/**
 * Track H — busy/progress overlay.
 *
 * Covers the canvas area with a semi-transparent scrim and a small card:
 * operation title, stage line, a determinate bar (indeterminate sweep when the
 * reported fraction is < 0), a pixel-count / detail line, and a Cancel button
 * that only appears once a job has run for ~800ms (short jobs never flash it).
 * show / update / hide are all idempotent.
 *
 * Styling is self-contained (injected <style>, `mopa-progress-` prefix) and
 * reads track G's design tokens via var(--token, fallback) when present.
 */

const PROGRESS_STYLE_ID = "mopa-progress-style";
const CANCEL_DELAY_MS = 800;

function injectProgressStyle(): void {
  if (typeof document === "undefined" || document.getElementById(PROGRESS_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = PROGRESS_STYLE_ID;
  el.textContent = `
.mopa-progress {
  position: absolute;
  inset: 0;
  z-index: 40;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(10, 12, 18, 0.55);
  backdrop-filter: blur(1.5px);
  -webkit-backdrop-filter: blur(1.5px);
  pointer-events: auto;
}
.mopa-progress.mopa-progress-visible { display: flex; }
.mopa-progress-card {
  width: min(360px, 100%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 22px;
  border-radius: var(--radius, 9px);
  border: 1px solid var(--border, rgba(255,255,255,0.09));
  background: var(--surface-2, #212124);
  box-shadow: var(--shadow-lg, 0 18px 48px rgba(0,0,0,0.55));
  color: var(--text-1, rgba(255,255,255,0.87));
  font-family: var(--font, system-ui, sans-serif);
}
.mopa-progress-title {
  font-size: 14px;
  font-weight: 650;
}
.mopa-progress-stage {
  font-size: 12.5px;
  color: var(--text-2, rgba(255,255,255,0.6));
  min-height: 16px;
}
.mopa-progress-track {
  position: relative;
  height: 6px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--surface-4, #34343a);
}
.mopa-progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0%;
  border-radius: 6px;
  background: var(--accent, #6ea8ff);
  transition: width 0.16s ease;
}
.mopa-progress.mopa-progress-indeterminate .mopa-progress-fill {
  width: 34%;
  transition: none;
  animation: mopa-progress-sweep 1.05s ease-in-out infinite;
}
@keyframes mopa-progress-sweep {
  0%   { left: -34%; }
  100% { left: 100%; }
}
/* Reduced motion: no infinite sweep. Indeterminate state shows a static,
   full-width dimmed bar (still announced via aria-live) instead of animating. */
@media (prefers-reduced-motion: reduce) {
  .mopa-progress-fill { transition: none; }
  .mopa-progress.mopa-progress-indeterminate .mopa-progress-fill {
    left: 0;
    width: 100%;
    opacity: 0.45;
    animation: none;
  }
}
.mopa-progress-detail {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 11.5px;
  color: var(--text-3, rgba(255,255,255,0.38));
  min-height: 15px;
}
.mopa-progress-cancel {
  align-self: flex-end;
  display: none;
  margin-top: 2px;
  padding: 6px 13px;
  border-radius: var(--radius-sm, 6px);
  border: 1px solid var(--border, rgba(255,255,255,0.09));
  background: var(--surface-3, #2a2a2e);
  color: var(--text-1, rgba(255,255,255,0.87));
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 550;
  cursor: pointer;
}
.mopa-progress-cancel:hover { border-color: var(--border-strong, rgba(255,255,255,0.17)); }
.mopa-progress.mopa-progress-cancellable .mopa-progress-cancel { display: inline-flex; }
`;
  document.head.appendChild(el);
}

export class ProgressOverlay {
  private root: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private stageEl: HTMLDivElement;
  private fillEl: HTMLDivElement;
  private trackEl!: HTMLDivElement;
  private detailEl: HTMLDivElement;
  private cancelEl: HTMLButtonElement;
  private onCancel: () => void;

  private visible = false;
  private cancelTimer = 0;

  constructor(host: HTMLElement, onCancel: () => void) {
    injectProgressStyle();
    this.onCancel = onCancel;

    const root = document.createElement("div");
    root.className = "mopa-progress";
    root.setAttribute("role", "status");
    root.setAttribute("aria-live", "polite");

    const card = document.createElement("div");
    card.className = "mopa-progress-card";

    this.titleEl = document.createElement("div");
    this.titleEl.className = "mopa-progress-title";

    this.stageEl = document.createElement("div");
    this.stageEl.className = "mopa-progress-stage";

    const track = document.createElement("div");
    track.className = "mopa-progress-track";
    // Expose the bar to assistive tech: determinate carries aria-valuenow;
    // indeterminate drops it (per ARIA) and relies on the aria-live stage line.
    track.setAttribute("role", "progressbar");
    track.setAttribute("aria-valuemin", "0");
    track.setAttribute("aria-valuemax", "100");
    this.trackEl = track;
    this.fillEl = document.createElement("div");
    this.fillEl.className = "mopa-progress-fill";
    track.appendChild(this.fillEl);

    this.detailEl = document.createElement("div");
    this.detailEl.className = "mopa-progress-detail";

    this.cancelEl = document.createElement("button");
    this.cancelEl.type = "button";
    this.cancelEl.className = "mopa-progress-cancel";
    this.cancelEl.textContent = "Cancel";
    this.cancelEl.addEventListener("click", this.handleCancel);

    card.append(this.titleEl, this.stageEl, track, this.detailEl, this.cancelEl);
    root.appendChild(card);
    host.appendChild(root);
    this.root = root;
  }

  show(operation: string): void {
    this.titleEl.textContent = operation || "Working";
    if (!this.visible) {
      this.visible = true;
      this.stageEl.textContent = "";
      this.detailEl.textContent = "";
      this.setIndeterminate(true);
      this.root.classList.remove("mopa-progress-cancellable");
      this.root.classList.add("mopa-progress-visible");
      this.armCancel();
    }
  }

  update(stage: string, fraction: number, detail: string): void {
    // Tolerate being called before show(); surface it either way.
    if (!this.visible) this.show(this.titleEl.textContent || "Working");
    this.stageEl.textContent = stage || "";
    this.detailEl.textContent = detail || "";
    if (!Number.isFinite(fraction) || fraction < 0) {
      this.setIndeterminate(true);
    } else {
      this.setIndeterminate(false);
      const pct = Math.max(0, Math.min(1, fraction)) * 100;
      this.fillEl.style.width = `${pct}%`;
      const rounded = Math.round(pct);
      this.trackEl.setAttribute("aria-valuenow", String(rounded));
      this.trackEl.setAttribute("aria-valuetext", `${rounded}%${stage ? ` — ${stage}` : ""}`);
    }
  }

  hide(): void {
    this.clearCancelTimer();
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.remove("mopa-progress-visible", "mopa-progress-cancellable", "mopa-progress-indeterminate");
  }

  /** Remove the overlay element and its listeners entirely. */
  destroy(): void {
    this.clearCancelTimer();
    this.cancelEl.removeEventListener("click", this.handleCancel);
    this.root.remove();
  }

  // ------------------------------------------------------------ internals ---

  private setIndeterminate(on: boolean): void {
    this.root.classList.toggle("mopa-progress-indeterminate", on);
    if (on) {
      this.fillEl.style.width = "";
      // Per ARIA, an indeterminate progressbar omits aria-valuenow.
      this.trackEl.removeAttribute("aria-valuenow");
      this.trackEl.setAttribute("aria-valuetext", "Working…");
    }
  }

  private armCancel(): void {
    this.clearCancelTimer();
    if (typeof setTimeout === "undefined") return;
    this.cancelTimer = setTimeout(() => {
      this.cancelTimer = 0;
      if (this.visible) this.root.classList.add("mopa-progress-cancellable");
    }, CANCEL_DELAY_MS) as unknown as number;
  }

  private clearCancelTimer(): void {
    if (this.cancelTimer) {
      clearTimeout(this.cancelTimer);
      this.cancelTimer = 0;
    }
  }

  private handleCancel = (): void => {
    this.stageEl.textContent = "Cancelling…";
    this.setIndeterminate(true);
    this.onCancel();
  };
}
